import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SURGICAL_ACT_LIMITS } from "../../../classes/constants/surgicalActConstants";
import { usePlanContext } from "../../../classes/contexts/PlanContext";
import { ButtonSize } from "../../../classes/enums/ButtonEnum";
import IconsEnum from "../../../classes/enums/IconsEnum";
import {
  TargetTypeEnum,
  getBaseTargetType,
  getCustomTargetTypeForBase,
  isCustomTargetType,
} from "../../../classes/enums/SppEnum";
import { ActNatures } from "../../../classes/enums/SurgicalActsEnum";
import Button from "../../../components/button/Button";
import IconButton from "../../../components/iconButton/IconButton";
import IncrementDecrementButtons from "../../../components/incrementDecrementButtons/IncrementDecrementButtons";
import LabeledMeasurement from "../../../components/labeledMeasurement/LabeledMeasurement";
import LabeledSlider, {
  LABELED_SLIDER_TYPES,
} from "../../../components/labeledSlider/LabeledSlider";
import MeasurementDisplay from "../../../components/measurementDisplay/MeasurementDisplay";
import { TooltipOrientation } from "../../../components/tooltip/Tooltip";
import useTranslation from "../../../hooks/i18n/useTranslation";
import useSurgicalActActions from "../../../hooks/useSurgicalActActions";
import TargetType from "../../common/spp/TargetType";
import "./AlignPopupParameterSelector.css";

const SUGGESTED_KNEE_ANGLE = 3;
const DEBOUNCE_MS = 2000;

const GlobalAlignment = ({
  onConfirm = null,
  onUpdate = null,
  existingGlobalAlignment = null,
  preOpGlobalAlignment = null,
  saasGlobalAlignment = null,
  gapGlobalAlignment = null,
  measurementConfigs = {},
}) => {
  const { t } = useTranslation();
  const initialPelvicTilt = existingGlobalAlignment?.pelvicTilt;
  const initialTargetPelvicTilt = existingGlobalAlignment?.targetPelvicTilt;
  const initialKneeAngle = existingGlobalAlignment?.kneeAngle;
  const initialLumbarLordosis = existingGlobalAlignment?.lumbarLordosis;
  const initialSacralSlope = existingGlobalAlignment?.sacralSlope;
  const initialT1pa = existingGlobalAlignment?.t1pa;
  const initialGlobalTilt = existingGlobalAlignment?.globalTilt;
  const initialPiLl = existingGlobalAlignment?.piLl;

  const initialMethod = existingGlobalAlignment?.method || TargetTypeEnum.GAP;

  const { currentPlanId } = usePlanContext();

  const { HideGlobalAlignmentTool } = useSurgicalActActions(currentPlanId);

  const getInitialAlignment = () => {
    const baseMethod = getBaseTargetType(initialMethod);
    if (baseMethod === TargetTypeEnum.SAAS) {
      return saasGlobalAlignment;
    } else if (baseMethod === TargetTypeEnum.GAP) {
      return gapGlobalAlignment;
    }
    return preOpGlobalAlignment;
  };

  const initialAlignment = getInitialAlignment();

  const [pelvicTiltValue, setPelvicTiltValue] = useState(
    initialPelvicTilt ?? preOpGlobalAlignment?.pelvicTilt ?? 0
  );

  const [kneeAngleValue, setKneeAngleValue] = useState(
    initialKneeAngle ?? preOpGlobalAlignment?.kneeAngle ?? 0
  );

  const [targetPelvicTiltValue, setTargetPelvicTiltValue] = useState(
    initialTargetPelvicTilt ?? initialAlignment?.targetPelvicTilt ?? 0
  );

  const [lumbarLordosisValue, setLumbarLordosisValue] = useState(
    initialLumbarLordosis ?? initialAlignment?.lumbarLordosis ?? 0
  );

  const [sacralSlopeValue, setSacralSlopeValue] = useState(
    initialSacralSlope ?? initialAlignment?.sacralSlope ?? 0
  );

  const [t1paValue, setT1paValue] = useState(
    initialT1pa ?? initialAlignment?.t1pa ?? 0
  );

  const [piLlValue, setPiLlValue] = useState(
    initialPiLl ?? initialAlignment?.piLl ?? 0
  );

  const [globalTiltValue, setGlobalTiltValue] = useState(
    initialGlobalTilt ?? initialAlignment?.globalTilt ?? 0
  );

  const [selectedMethod, setSelectedMethod] = useState(initialMethod);
  const [isConfirming, setIsConfirming] = useState(false);
  const [showPelvicTiltOverlay, setShowPelvicTiltOverlay] = useState(false);
  const [showKneeAngleOverlay, setShowKneeAngleOverlay] = useState(false);

  const [pelvicTiltRange] = useState(SURGICAL_ACT_LIMITS.GLOBAL_ALIGNMENT.PELVIC_TILT);
  const [targetPelvicTiltRange] = useState(SURGICAL_ACT_LIMITS.GLOBAL_ALIGNMENT.TARGET_PELVIC_TILT);
  const [kneeAngleRange] = useState(SURGICAL_ACT_LIMITS.GLOBAL_ALIGNMENT.KNEE_ANGLE);
  const [t1paRange] = useState(SURGICAL_ACT_LIMITS.GLOBAL_ALIGNMENT.T1PA);
  const [piLlRange] = useState(SURGICAL_ACT_LIMITS.GLOBAL_ALIGNMENT.PI_LL);
  const [sacralSlopeRange] = useState(SURGICAL_ACT_LIMITS.GLOBAL_ALIGNMENT.SACRAL_SLOPE);
  const [globalTiltRange] = useState(SURGICAL_ACT_LIMITS.GLOBAL_ALIGNMENT.GLOBAL_TILT);

  const lumbarLordosisRange = useMemo(() => {
    const pi = preOpGlobalAlignment?.pelvicIncidence;
    if (pi == null) return null;
    const delta = SURGICAL_ACT_LIMITS.GLOBAL_ALIGNMENT.LUMBAR_LORDOSIS_PI_DELTA;
    return { min: Math.round(pi - delta), max: Math.round(pi + delta) };
  }, [preOpGlobalAlignment?.pelvicIncidence]);

  const isPiValid = lumbarLordosisRange !== null;

  const pelvicTiltIncrementRef = useRef(null);
  const pelvicTiltDecrementRef = useRef(null);
  const targetPelvicTiltIncrementRef = useRef(null);
  const targetPelvicTiltDecrementRef = useRef(null);
  const kneeAngleIncrementRef = useRef(null);
  const kneeAngleDecrementRef = useRef(null);
  const lumbarLordosisIncrementRef = useRef(null);
  const lumbarLordosisDecrementRef = useRef(null);
  const sacralSlopeIncrementRef = useRef(null);
  const sacralSlopeDecrementRef = useRef(null);
  const t1paIncrementRef = useRef(null);
  const t1paDecrementRef = useRef(null);
  const piLlIncrementRef = useRef(null);
  const piLlDecrementRef = useRef(null);
  const globalTiltIncrementRef = useRef(null);
  const globalTiltDecrementRef = useRef(null);

  const hasInitializedValues = useRef(false);
  const isFirstUpdate = useRef(true);
  const updateDebounceTimerRef = useRef(null);

  const INITIAL_DELAY = 500;
  const INCREMENT_INTERVAL = 50;

  const isPreopPelvicTiltValid = useMemo(() => {
    return (
      preOpGlobalAlignment?.pelvicTilt !== undefined &&
      preOpGlobalAlignment?.pelvicTilt !== null
    );
  }, [preOpGlobalAlignment]);

  const isPreopKneeAngleValid = useMemo(() => {
    return (
      preOpGlobalAlignment?.kneeAngle !== undefined &&
      preOpGlobalAlignment?.kneeAngle !== null
    );
  }, [preOpGlobalAlignment]);

  const isSaasTargetPelvicTiltValid = useMemo(() => {
    return (
      saasGlobalAlignment?.targetPelvicTilt !== undefined &&
      saasGlobalAlignment?.targetPelvicTilt !== null
    );
  }, [saasGlobalAlignment]);

  const isSaasT1paValid = useMemo(() => {
    return (
      saasGlobalAlignment?.t1pa !== undefined &&
      saasGlobalAlignment?.t1pa !== null
    );
  }, [saasGlobalAlignment]);

  const isSaasPiLlValid = useMemo(() => {
    return (
      saasGlobalAlignment?.piLl !== undefined &&
      saasGlobalAlignment?.piLl !== null
    );
  }, [saasGlobalAlignment]);

  const isGapLumbarLordosisValid = useMemo(() => {
    return (
      gapGlobalAlignment?.lumbarLordosis !== undefined &&
      gapGlobalAlignment?.lumbarLordosis !== null
    );
  }, [gapGlobalAlignment]);

  const isGapSacralSlopeValid = useMemo(() => {
    return (
      gapGlobalAlignment?.sacralSlope !== undefined &&
      gapGlobalAlignment?.sacralSlope !== null
    );
  }, [gapGlobalAlignment]);

  const isGapGlobalTiltValid = useMemo(() => {
    return (
      gapGlobalAlignment?.globalTilt !== undefined &&
      gapGlobalAlignment?.globalTilt !== null
    );
  }, [gapGlobalAlignment]);

  useEffect(() => {
    const currentMethod = existingGlobalAlignment?.method || initialMethod;
    const baseMethod = getBaseTargetType(currentMethod);
    const targetAlignment =
      baseMethod === TargetTypeEnum.SAAS
        ? saasGlobalAlignment
        : baseMethod === TargetTypeEnum.GAP
          ? gapGlobalAlignment
          : preOpGlobalAlignment;

    setPelvicTiltValue(
      existingGlobalAlignment?.pelvicTilt ??
        preOpGlobalAlignment?.pelvicTilt ??
        initialPelvicTilt ??
        0
    );
    setKneeAngleValue(
      existingGlobalAlignment?.kneeAngle ??
        preOpGlobalAlignment?.kneeAngle ??
        initialKneeAngle ??
        0
    );

    setLumbarLordosisValue(
      existingGlobalAlignment?.lumbarLordosis ??
        targetAlignment?.lumbarLordosis ??
        initialLumbarLordosis ??
        0
    );
    setSacralSlopeValue(
      existingGlobalAlignment?.sacralSlope ??
        targetAlignment?.sacralSlope ??
        initialSacralSlope ??
        0
    );
    setTargetPelvicTiltValue(
      existingGlobalAlignment?.targetPelvicTilt ??
        targetAlignment?.targetPelvicTilt ??
        initialTargetPelvicTilt ??
        0
    );
    setT1paValue(
      existingGlobalAlignment?.t1pa ?? targetAlignment?.t1pa ?? initialT1pa ?? 0
    );
    setPiLlValue(
      existingGlobalAlignment?.piLl ?? targetAlignment?.piLl ?? initialPiLl ?? 0
    );
    setGlobalTiltValue(
      existingGlobalAlignment?.globalTilt ??
        targetAlignment?.globalTilt ??
        initialGlobalTilt ??
        0
    );
    setSelectedMethod(currentMethod);

    hasInitializedValues.current = true;
  }, [
    existingGlobalAlignment?.kneeAngle,
    existingGlobalAlignment?.lumbarLordosis,
    existingGlobalAlignment?.sacralSlope,
    existingGlobalAlignment?.globalTilt,
    existingGlobalAlignment?.method,
    existingGlobalAlignment?.pelvicTilt,
    existingGlobalAlignment?.targetPelvicTilt,
    existingGlobalAlignment?.t1pa,
    existingGlobalAlignment?.piLl,
    initialKneeAngle,
    initialLumbarLordosis,
    initialSacralSlope,
    initialTargetPelvicTilt,
    initialT1pa,
    initialPiLl,
    initialGlobalTilt,
    initialMethod,
    initialPelvicTilt,
    preOpGlobalAlignment,
    saasGlobalAlignment,
    gapGlobalAlignment,
  ]);

  useEffect(() => {
    if (!hasInitializedValues.current) {
      return;
    }

    if (onUpdate) {
      if (updateDebounceTimerRef.current) {
        clearTimeout(updateDebounceTimerRef.current);
      }

      const updateData = {
        pelvicTilt: pelvicTiltValue,
        kneeAngle: kneeAngleValue,
        lumbarLordosis: lumbarLordosisValue,
        sacralSlope: sacralSlopeValue,
        targetPelvicTilt: targetPelvicTiltValue,
        t1pa: t1paValue,
        piLl: piLlValue,
        globalTilt: globalTiltValue,
        method: selectedMethod,
        pelvicIncidence: preOpGlobalAlignment?.pelvicIncidence ?? null,
        age: preOpGlobalAlignment?.age ?? null,
      };

      if (isFirstUpdate.current) {
        isFirstUpdate.current = false;
        onUpdate(updateData);
      } else {
        updateDebounceTimerRef.current = setTimeout(() => {
          onUpdate(updateData);
        }, DEBOUNCE_MS);
      }
    }

    return () => {
      if (updateDebounceTimerRef.current) {
        clearTimeout(updateDebounceTimerRef.current);
      }
    };
  }, [
    onUpdate,
    pelvicTiltValue,
    kneeAngleValue,
    lumbarLordosisValue,
    sacralSlopeValue,
    targetPelvicTiltValue,
    t1paValue,
    piLlValue,
    globalTiltValue,
    selectedMethod,
    preOpGlobalAlignment?.pelvicIncidence,
    preOpGlobalAlignment?.age,
  ]);

  useEffect(() => {
    return () => {
      HideGlobalAlignmentTool(currentPlanId);
    };
  }, [HideGlobalAlignmentTool, currentPlanId]);

  useEffect(() => {
    const baseMethod = getBaseTargetType(selectedMethod);

    if (
      baseMethod !== TargetTypeEnum.GAP &&
      baseMethod !== TargetTypeEnum.SAAS
    ) {
      return;
    }

    const referenceValues =
      baseMethod === TargetTypeEnum.SAAS
        ? saasGlobalAlignment
        : gapGlobalAlignment;

    if (!referenceValues) {
      return;
    }

    const valuesMatch = (current, reference) => {
      if (reference === undefined) return true;
      return Math.round(current) === Math.round(reference);
    };

    const hasChanges =
      (baseMethod === TargetTypeEnum.SAAS &&
        (!valuesMatch(piLlValue, referenceValues.piLl) ||
          !valuesMatch(
            targetPelvicTiltValue,
            referenceValues.targetPelvicTilt
          ) ||
          !valuesMatch(t1paValue, referenceValues.t1pa))) ||
      (baseMethod === TargetTypeEnum.GAP &&
        (!valuesMatch(lumbarLordosisValue, referenceValues.lumbarLordosis) ||
          !valuesMatch(sacralSlopeValue, referenceValues.sacralSlope) ||
          !valuesMatch(globalTiltValue, referenceValues.globalTilt)));

    let targetMethod = null;
    if (hasChanges && !isCustomTargetType(selectedMethod)) {
      targetMethod = getCustomTargetTypeForBase(baseMethod);
    } else if (!hasChanges && isCustomTargetType(selectedMethod)) {
      targetMethod = baseMethod;
    }

    if (targetMethod && targetMethod !== selectedMethod) {
      setSelectedMethod(targetMethod);
    }
  }, [
    kneeAngleValue,
    lumbarLordosisValue,
    sacralSlopeValue,
    targetPelvicTiltValue,
    t1paValue,
    piLlValue,
    globalTiltValue,
    selectedMethod,
    saasGlobalAlignment,
    gapGlobalAlignment,
  ]);

  useEffect(() => {
    if (!hasInitializedValues.current) {
      return;
    }

    if (!isCustomTargetType(selectedMethod)) {
      return;
    }

    const baseMethod = getBaseTargetType(selectedMethod);

    const valuesMatch = (current, reference) => {
      if (reference === undefined) return true;
      return Math.round(current) === Math.round(reference);
    };

    const valuesMatchMethod = (refAlignment, isSaasMethod) => {
      if (!refAlignment) {
        return false;
      }

      if (isSaasMethod) {
        return (
          valuesMatch(piLlValue, refAlignment.piLl) &&
          valuesMatch(targetPelvicTiltValue, refAlignment.targetPelvicTilt) &&
          valuesMatch(t1paValue, refAlignment.t1pa)
        );
      } else {
        return (
          valuesMatch(lumbarLordosisValue, refAlignment.lumbarLordosis) &&
          valuesMatch(sacralSlopeValue, refAlignment.sacralSlope) &&
          valuesMatch(globalTiltValue, refAlignment.globalTilt)
        );
      }
    };

    let targetMethod = null;
    if (baseMethod === TargetTypeEnum.SAAS) {
      if (valuesMatchMethod(saasGlobalAlignment, true)) {
        targetMethod = TargetTypeEnum.SAAS;
      }
    } else if (baseMethod === TargetTypeEnum.GAP) {
      if (valuesMatchMethod(gapGlobalAlignment, false)) {
        targetMethod = TargetTypeEnum.GAP;
      }
    }

    if (targetMethod && targetMethod !== selectedMethod) {
      setSelectedMethod(targetMethod);
    }
  }, [
    kneeAngleValue,
    lumbarLordosisValue,
    sacralSlopeValue,
    targetPelvicTiltValue,
    t1paValue,
    piLlValue,
    globalTiltValue,
    selectedMethod,
    saasGlobalAlignment,
    gapGlobalAlignment,
  ]);

  const clampValue = useCallback((value, min, max) => {
    return Math.min(Math.max(Number(value) || 0, min), max);
  }, []);

  const handlePelvicTiltChange = useCallback(
    newValue =>
      setPelvicTiltValue(
        clampValue(newValue, pelvicTiltRange.min, pelvicTiltRange.max)
      ),
    [clampValue, pelvicTiltRange]
  );

  const handleKneeAngleChange = useCallback(
    newValue =>
      setKneeAngleValue(
        clampValue(newValue, kneeAngleRange.min, kneeAngleRange.max)
      ),
    [clampValue, kneeAngleRange]
  );

  const handleLumbarLordosisChange = useCallback(
    newValue => {
      if (!lumbarLordosisRange) return;
      setLumbarLordosisValue(
        clampValue(newValue, lumbarLordosisRange.min, lumbarLordosisRange.max)
      );
    },
    [clampValue, lumbarLordosisRange]
  );

  const handleSacralSlopeChange = useCallback(
    newValue =>
      setSacralSlopeValue(
        clampValue(newValue, sacralSlopeRange.min, sacralSlopeRange.max)
      ),
    [clampValue, sacralSlopeRange]
  );

  const handleTargetPelvicTiltChange = useCallback(
    newValue =>
      setTargetPelvicTiltValue(
        clampValue(
          newValue,
          targetPelvicTiltRange.min,
          targetPelvicTiltRange.max
        )
      ),
    [clampValue, targetPelvicTiltRange]
  );

  const handleT1paChange = useCallback(
    newValue =>
      setT1paValue(clampValue(newValue, t1paRange.min, t1paRange.max)),
    [clampValue, t1paRange]
  );

  const handleGlobalTiltChange = useCallback(
    newValue =>
      setGlobalTiltValue(
        clampValue(newValue, globalTiltRange.min, globalTiltRange.max)
      ),
    [clampValue, globalTiltRange]
  );

  const handlePiLlChange = useCallback(
    newValue =>
      setPiLlValue(clampValue(newValue, piLlRange.min, piLlRange.max)),
    [clampValue, piLlRange]
  );

  const handleSelecedMethodChange = useCallback(
    newMethod => {
      setSelectedMethod(newMethod);
      const baseMethod = getBaseTargetType(newMethod);
      switch (baseMethod) {
        case TargetTypeEnum.SAAS:
          setLumbarLordosisValue(saasGlobalAlignment?.lumbarLordosis ?? 0);
          setSacralSlopeValue(saasGlobalAlignment?.sacralSlope ?? 0);
          setTargetPelvicTiltValue(saasGlobalAlignment?.targetPelvicTilt ?? 0);
          setT1paValue(saasGlobalAlignment?.t1pa ?? 0);
          setPiLlValue(saasGlobalAlignment?.piLl ?? 0);
          setGlobalTiltValue(saasGlobalAlignment?.globalTilt ?? 0);
          break;
        case TargetTypeEnum.GAP:
          setLumbarLordosisValue(gapGlobalAlignment?.lumbarLordosis ?? 0);
          setSacralSlopeValue(gapGlobalAlignment?.sacralSlope ?? 0);
          setTargetPelvicTiltValue(gapGlobalAlignment?.targetPelvicTilt ?? 0);
          setT1paValue(gapGlobalAlignment?.t1pa ?? 0);
          setPiLlValue(gapGlobalAlignment?.piLl ?? 0);
          setGlobalTiltValue(gapGlobalAlignment?.globalTilt ?? 0);
          break;
        case TargetTypeEnum.PRE_OP:
          setPelvicTiltValue(preOpGlobalAlignment?.pelvicTilt ?? 0);
          setKneeAngleValue(preOpGlobalAlignment?.kneeAngle ?? 0);
          setLumbarLordosisValue(preOpGlobalAlignment?.lumbarLordosis ?? 0);
          setSacralSlopeValue(preOpGlobalAlignment?.sacralSlope ?? 0);
          setTargetPelvicTiltValue(preOpGlobalAlignment?.targetPelvicTilt ?? 0);
          setT1paValue(preOpGlobalAlignment?.t1pa ?? 0);
          setPiLlValue(preOpGlobalAlignment?.piLl ?? 0);
          setGlobalTiltValue(preOpGlobalAlignment?.globalTilt ?? 0);
          break;
        default:
          break;
      }
    },
    [
      gapGlobalAlignment?.lumbarLordosis,
      gapGlobalAlignment?.sacralSlope,
      gapGlobalAlignment?.globalTilt,
      gapGlobalAlignment?.targetPelvicTilt,
      gapGlobalAlignment?.t1pa,
      gapGlobalAlignment?.piLl,
      preOpGlobalAlignment?.kneeAngle,
      preOpGlobalAlignment?.pelvicTilt,
      preOpGlobalAlignment?.lumbarLordosis,
      preOpGlobalAlignment?.sacralSlope,
      preOpGlobalAlignment?.globalTilt,
      preOpGlobalAlignment?.targetPelvicTilt,
      preOpGlobalAlignment?.t1pa,
      preOpGlobalAlignment?.piLl,
      saasGlobalAlignment?.lumbarLordosis,
      saasGlobalAlignment?.sacralSlope,
      saasGlobalAlignment?.globalTilt,
      saasGlobalAlignment?.targetPelvicTilt,
      saasGlobalAlignment?.t1pa,
      saasGlobalAlignment?.piLl,
    ]
  );

  const baseSelectedMethod = useMemo(
    () => getBaseTargetType(selectedMethod),
    [selectedMethod]
  );

  const isSaasMode = useMemo(
    () => baseSelectedMethod === TargetTypeEnum.SAAS,
    [baseSelectedMethod]
  );

  const isCustomButtonDisabled = useMemo(() => {
    if (
      baseSelectedMethod !== TargetTypeEnum.GAP &&
      baseSelectedMethod !== TargetTypeEnum.SAAS
    ) {
      return false;
    }

    const referenceValues =
      baseSelectedMethod === TargetTypeEnum.SAAS
        ? saasGlobalAlignment
        : gapGlobalAlignment;

    if (!referenceValues) {
      return true;
    }

    const valuesMatch = (current, reference) => {
      if (reference === undefined) return true;
      return Math.round(current) === Math.round(reference);
    };

    if (baseSelectedMethod === TargetTypeEnum.SAAS) {
      return (
        valuesMatch(piLlValue, referenceValues.piLl) &&
        valuesMatch(targetPelvicTiltValue, referenceValues.targetPelvicTilt) &&
        valuesMatch(t1paValue, referenceValues.t1pa)
      );
    } else {
      return (
        valuesMatch(lumbarLordosisValue, referenceValues.lumbarLordosis) &&
        valuesMatch(sacralSlopeValue, referenceValues.sacralSlope) &&
        valuesMatch(globalTiltValue, referenceValues.globalTilt)
      );
    }
  }, [
    baseSelectedMethod,
    saasGlobalAlignment,
    gapGlobalAlignment,
    piLlValue,
    targetPelvicTiltValue,
    t1paValue,
    lumbarLordosisValue,
    sacralSlopeValue,
    globalTiltValue,
  ]);

  const pelvicTiltMarkers = useMemo(() => {
    const markers = [];

    if (baseSelectedMethod === TargetTypeEnum.SAAS) {
      if (
        saasGlobalAlignment?.pelvicTilt !== undefined &&
        saasGlobalAlignment?.pelvicTilt !== null
      ) {
        markers.push({
          label: "SAAS",
          value: saasGlobalAlignment.pelvicTilt,
          color: "var(--MDsim-highlight-color)",
        });
      }
    } else if (baseSelectedMethod === TargetTypeEnum.GAP) {
      if (
        gapGlobalAlignment?.pelvicTilt !== undefined &&
        gapGlobalAlignment?.pelvicTilt !== null
      ) {
        markers.push({
          label: "GAP",
          value: gapGlobalAlignment.pelvicTilt,
          color: "var(--MDsim-highlight-color)",
        });
      }
    }

    return markers;
  }, [
    baseSelectedMethod,
    saasGlobalAlignment?.pelvicTilt,
    gapGlobalAlignment?.pelvicTilt,
  ]);

  const kneeAngleMarkers = useMemo(() => [], []);
  const lumbarLordosisMarkers = useMemo(() => [], []);
  const sacralSlopeMarkers = useMemo(() => [], []);
  const globalTiltMarkers = useMemo(() => [], []);
  const targetPelvicTiltMarkers = useMemo(() => [], []);
  const t1paMarkers = useMemo(() => [], []);
  const piLlMarkers = useMemo(() => [], []);

  const startIncrement = (ref, setValue, range) => {
    if (!range) return;
    clearInterval(ref.current);
    setValue(prev => clampValue(prev + 1, range.min, range.max));
    ref.current = setTimeout(() => {
      ref.current = setInterval(() => {
        setValue(prev => clampValue(prev + 1, range.min, range.max));
      }, INCREMENT_INTERVAL);
    }, INITIAL_DELAY);
  };

  const startDecrement = (ref, setValue, range) => {
    if (!range) return;
    clearInterval(ref.current);
    setValue(prev => clampValue(prev - 1, range.min, range.max));
    ref.current = setTimeout(() => {
      ref.current = setInterval(() => {
        setValue(prev => clampValue(prev - 1, range.min, range.max));
      }, INCREMENT_INTERVAL);
    }, INITIAL_DELAY);
  };

  const stopChange = ref => {
    if (ref.current) {
      clearInterval(ref.current);
      ref.current = null;
    }
  };

  const normalizedSppConfigs = useMemo(() => {
    const configs = Object.values(measurementConfigs || {}).flatMap(group =>
      Array.isArray(group) ? group : []
    );

    const normalize = value =>
      `${value ?? ""}`.toLowerCase().replace(/[^a-z0-9]/g, "");

    const map = new Map();
    configs.forEach(config => {
      const keys = [config.shortName, config.fullName, config.name, config.key];
      keys.forEach(key => {
        const normalized = normalize(key);
        if (normalized) {
          map.set(normalized, config);
        }
      });
    });

    return { map, normalize };
  }, [measurementConfigs]);

  const getSppTooltip = useCallback(
    label => {
      const normalizedLabel = normalizedSppConfigs.normalize(label);
      const config = normalizedSppConfigs.map.get(normalizedLabel);
      return (
        config?.description ||
        config?.fullName ||
        config?.name ||
        config?.key ||
        label
      );
    },
    [normalizedSppConfigs]
  );

  const hasChanges = useMemo(() => {
    return (
      pelvicTiltValue !== initialPelvicTilt ||
      kneeAngleValue !== initialKneeAngle ||
      lumbarLordosisValue !== initialLumbarLordosis ||
      sacralSlopeValue !== initialSacralSlope ||
      targetPelvicTiltValue !== initialTargetPelvicTilt ||
      t1paValue !== initialT1pa ||
      piLlValue !== initialPiLl ||
      globalTiltValue !== initialGlobalTilt ||
      selectedMethod !== initialMethod
    );
  }, [
    pelvicTiltValue,
    kneeAngleValue,
    lumbarLordosisValue,
    sacralSlopeValue,
    targetPelvicTiltValue,
    t1paValue,
    piLlValue,
    globalTiltValue,
    selectedMethod,
    initialPelvicTilt,
    initialKneeAngle,
    initialLumbarLordosis,
    initialSacralSlope,
    initialTargetPelvicTilt,
    initialT1pa,
    initialPiLl,
    initialGlobalTilt,
    initialMethod,
  ]);

  const isConfirmDisabled = useMemo(() => {
    const isLumbarLordosisOutOfRange =
      !isPiValid || !lumbarLordosisRange
        ? true
        : lumbarLordosisValue < lumbarLordosisRange.min ||
          lumbarLordosisValue > lumbarLordosisRange.max;
    return (
      pelvicTiltValue < pelvicTiltRange.min ||
      pelvicTiltValue > pelvicTiltRange.max ||
      kneeAngleValue < kneeAngleRange.min ||
      kneeAngleValue > kneeAngleRange.max ||
      isLumbarLordosisOutOfRange ||
      sacralSlopeValue < sacralSlopeRange.min ||
      sacralSlopeValue > sacralSlopeRange.max ||
      targetPelvicTiltValue < targetPelvicTiltRange.min ||
      targetPelvicTiltValue > targetPelvicTiltRange.max ||
      t1paValue < t1paRange.min ||
      t1paValue > t1paRange.max ||
      piLlValue < piLlRange.min ||
      piLlValue > piLlRange.max ||
      globalTiltValue < globalTiltRange.min ||
      globalTiltValue > globalTiltRange.max ||
      isConfirming ||
      !hasChanges
    );
  }, [
    pelvicTiltValue,
    pelvicTiltRange,
    kneeAngleValue,
    kneeAngleRange,
    lumbarLordosisValue,
    lumbarLordosisRange,
    isPiValid,
    sacralSlopeValue,
    sacralSlopeRange,
    targetPelvicTiltValue,
    targetPelvicTiltRange,
    t1paValue,
    t1paRange,
    piLlValue,
    piLlRange,
    globalTiltValue,
    globalTiltRange,
    isConfirming,
    hasChanges,
  ]);

  const handleConfirm = useCallback(async () => {
    if (isConfirming) return;

    if (!hasChanges) {
      return;
    }

    const isLumbarLordosisOutOfRange =
      !isPiValid || !lumbarLordosisRange
        ? true
        : lumbarLordosisValue < lumbarLordosisRange.min ||
          lumbarLordosisValue > lumbarLordosisRange.max;

    if (
      pelvicTiltValue < pelvicTiltRange.min ||
      pelvicTiltValue > pelvicTiltRange.max ||
      kneeAngleValue < kneeAngleRange.min ||
      kneeAngleValue > kneeAngleRange.max ||
      isLumbarLordosisOutOfRange ||
      sacralSlopeValue < sacralSlopeRange.min ||
      sacralSlopeValue > sacralSlopeRange.max ||
      targetPelvicTiltValue < targetPelvicTiltRange.min ||
      targetPelvicTiltValue > targetPelvicTiltRange.max ||
      t1paValue < t1paRange.min ||
      t1paValue > t1paRange.max ||
      piLlValue < piLlRange.min ||
      piLlValue > piLlRange.max ||
      globalTiltValue < globalTiltRange.min ||
      globalTiltValue > globalTiltRange.max
    ) {
      return;
    }

    setIsConfirming(true);
    try {
      const globalAlignmentAct = {
        actNature: ActNatures.GlobalAlignment,
        vertebra: null,
        value: null,
        pelvicTilt: pelvicTiltValue,
        kneeAngle: isPreopKneeAngleValid ? kneeAngleValue : null,
        lumbarLordosis: lumbarLordosisValue,
        sacralSlope: sacralSlopeValue,
        targetPelvicTilt: targetPelvicTiltValue,
        t1pa: t1paValue,
        piLl: piLlValue,
        globalTilt: globalTiltValue,
        method: selectedMethod,
        pelvicIncidence: preOpGlobalAlignment?.pelvicIncidence ?? null,
        age: preOpGlobalAlignment?.age ?? null,
        originalPelvicTilt: preOpGlobalAlignment?.pelvicTilt ?? null,
        originalKneeAngle: preOpGlobalAlignment?.kneeAngle ?? null,
        originalLumbarLordosis: preOpGlobalAlignment?.lumbarLordosis ?? null,
        originalSacralSlope: preOpGlobalAlignment?.sacralSlope ?? null,
        originalGlobalTilt: preOpGlobalAlignment?.globalTilt ?? null,
        originalTargetPelvicTilt:
          preOpGlobalAlignment?.targetPelvicTilt ?? null,
        originalT1pa: preOpGlobalAlignment?.t1pa ?? null,
        originalPiLl: preOpGlobalAlignment?.piLl ?? null,
      };

      if (onConfirm) {
        onConfirm(globalAlignmentAct);
      } else {
      }
    } catch (error) {
    } finally {
      setIsConfirming(false);
    }
  }, [
    isConfirming,
    hasChanges,
    pelvicTiltValue,
    kneeAngleValue,
    lumbarLordosisValue,
    sacralSlopeValue,
    targetPelvicTiltValue,
    t1paValue,
    piLlValue,
    globalTiltValue,
    selectedMethod,
    preOpGlobalAlignment?.pelvicIncidence,
    preOpGlobalAlignment?.age,
    preOpGlobalAlignment?.pelvicTilt,
    preOpGlobalAlignment?.kneeAngle,
    preOpGlobalAlignment?.lumbarLordosis,
    preOpGlobalAlignment?.sacralSlope,
    preOpGlobalAlignment?.globalTilt,
    preOpGlobalAlignment?.targetPelvicTilt,
    preOpGlobalAlignment?.t1pa,
    preOpGlobalAlignment?.piLl,
    onConfirm,
  ]);

  useEffect(
    () => () => {
      [
        pelvicTiltIncrementRef,
        pelvicTiltDecrementRef,
        targetPelvicTiltIncrementRef,
        targetPelvicTiltDecrementRef,
        kneeAngleIncrementRef,
        kneeAngleDecrementRef,
        lumbarLordosisIncrementRef,
        lumbarLordosisDecrementRef,
        sacralSlopeIncrementRef,
        sacralSlopeDecrementRef,
        t1paIncrementRef,
        t1paDecrementRef,
        piLlIncrementRef,
        piLlDecrementRef,
        globalTiltIncrementRef,
        globalTiltDecrementRef,
      ].forEach(ref => {
        if (ref.current) clearInterval(ref.current);
      });
    },
    []
  );

  return (
    <div className="AlignPopupParameterSelector-container">
      <div className="AlignPopupParameterSelector-scrollable-content">
        <div className="AlignPopupParameterSelector-controls-wrapper">
          <div className="AlignPopupParameterSelector-header">
            <div className="AlignPopupParameterSelector-subtitle App-text-medium ">
              Select the planning method between GAP and SAAS or define your own
              target.
            </div>
            <div className="AlignPopupParameterSelector-rod-header-row">
              <LabeledMeasurement
                label={"Pelvic Incidence"}
                value={
                  preOpGlobalAlignment?.pelvicIncidence
                    ? Math.round(preOpGlobalAlignment.pelvicIncidence)
                    : "--"
                }
                unit={"°"}
              ></LabeledMeasurement>
              <LabeledMeasurement
                label={"Age"}
                value={preOpGlobalAlignment?.age || "--"}
                unit={"yo"}
              ></LabeledMeasurement>
            </div>
          </div>

          <div className="AlignPopupParameterSelector-target-type-section">
            <div className="AlignPopupParameterSelector-target-type-title-section">
              <div className="App-title-widget">Define Target Spine:</div>
              <IconButton
                icon={IconsEnum.INFO}
                title={t("460")}
                onClick={e => e.stopPropagation()}
                isCircle
                size={ButtonSize.MICRO}
                isLightBlue={false}
                tooltipOrientation={TooltipOrientation.Right}
              />
            </div>
            <TargetType
              selectedTargetType={selectedMethod}
              onTargetTypeChange={handleSelecedMethodChange}
              disabledTargetTypes={
                isCustomButtonDisabled ? [TargetTypeEnum.CUSTOM] : []
              }
            />
            {isSaasMode ? (
              <>
                <div className="AlignPopupParameterSelector-item">
                  <div className="AlignPopupParameterSelector-first-row">
                    <LabeledSlider
                      label="PI-LL"
                      min={piLlRange.min}
                      max={piLlRange.max}
                      value={piLlValue}
                      type={LABELED_SLIDER_TYPES.HORIZONTAL}
                      onChange={handlePiLlChange}
                      disabled={isConfirming || !isSaasPiLlValid}
                      markers={piLlMarkers}
                      tooltip={getSppTooltip("PI-LL")}
                    />
                    <div className="AlignPopupParameterSelector-measurement-container">
                      <IncrementDecrementButtons
                        onIncrement={() =>
                          startIncrement(
                            piLlIncrementRef,
                            setPiLlValue,
                            piLlRange
                          )
                        }
                        onDecrement={() =>
                          startDecrement(
                            piLlDecrementRef,
                            setPiLlValue,
                            piLlRange
                          )
                        }
                        onIncrementEnd={() => stopChange(piLlIncrementRef)}
                        onDecrementEnd={() => stopChange(piLlDecrementRef)}
                        disabled={isConfirming || !isSaasPiLlValid}
                      />
                      <MeasurementDisplay
                        value={isSaasPiLlValid ? piLlValue : NaN}
                        unit="°"
                        disabled={isConfirming || !isSaasPiLlValid}
                      />
                    </div>
                  </div>
                  <div className="AlignPopupParameterSelector-second-row App-text-medium">
                    SAAS:{" "}
                    {saasGlobalAlignment?.piLl !== undefined &&
                    saasGlobalAlignment?.piLl !== null
                      ? `${Math.round(saasGlobalAlignment.piLl)}°`
                      : "--"}{" "}
                    | Original:{" "}
                    {preOpGlobalAlignment?.piLl !== undefined &&
                    preOpGlobalAlignment?.piLl !== null
                      ? `${Math.round(preOpGlobalAlignment.piLl)}°`
                      : "--"}
                  </div>
                </div>
                <div className="AlignPopupParameterSelector-item">
                  <div className="AlignPopupParameterSelector-first-row">
                    <LabeledSlider
                      label="PT"
                      min={targetPelvicTiltRange.min}
                      max={targetPelvicTiltRange.max}
                      value={targetPelvicTiltValue}
                      type={LABELED_SLIDER_TYPES.HORIZONTAL}
                      onChange={handleTargetPelvicTiltChange}
                      disabled={isConfirming || !isSaasTargetPelvicTiltValid}
                      markers={targetPelvicTiltMarkers}
                      tooltip={getSppTooltip("PT")}
                    />
                    <div className="AlignPopupParameterSelector-measurement-container">
                      <IncrementDecrementButtons
                        onIncrement={() =>
                          startIncrement(
                            targetPelvicTiltIncrementRef,
                            setTargetPelvicTiltValue,
                            targetPelvicTiltRange
                          )
                        }
                        onDecrement={() =>
                          startDecrement(
                            targetPelvicTiltDecrementRef,
                            setTargetPelvicTiltValue,
                            targetPelvicTiltRange
                          )
                        }
                        onIncrementEnd={() =>
                          stopChange(targetPelvicTiltIncrementRef)
                        }
                        onDecrementEnd={() =>
                          stopChange(targetPelvicTiltDecrementRef)
                        }
                        disabled={isConfirming || !isSaasTargetPelvicTiltValid}
                      />
                      <MeasurementDisplay
                        value={
                          isSaasTargetPelvicTiltValid
                            ? targetPelvicTiltValue
                            : NaN
                        }
                        unit="°"
                        disabled={isConfirming || !isSaasTargetPelvicTiltValid}
                      />
                    </div>
                  </div>
                  <div className="AlignPopupParameterSelector-second-row App-text-medium">
                    SAAS:{" "}
                    {saasGlobalAlignment?.targetPelvicTilt !== undefined &&
                    saasGlobalAlignment?.targetPelvicTilt !== null
                      ? `${Math.round(saasGlobalAlignment.targetPelvicTilt)}°`
                      : "--"}{" "}
                    | Original:{" "}
                    {preOpGlobalAlignment?.targetPelvicTilt !== undefined &&
                    preOpGlobalAlignment?.targetPelvicTilt !== null
                      ? `${Math.round(preOpGlobalAlignment.targetPelvicTilt)}°`
                      : "--"}
                  </div>
                </div>
                <div className="AlignPopupParameterSelector-item">
                  <div className="AlignPopupParameterSelector-first-row">
                    <LabeledSlider
                      label="TPA"
                      min={t1paRange.min}
                      max={t1paRange.max}
                      value={t1paValue}
                      type={LABELED_SLIDER_TYPES.HORIZONTAL}
                      onChange={handleT1paChange}
                      disabled={isConfirming || !isSaasT1paValid}
                      markers={t1paMarkers}
                      tooltip={getSppTooltip("TPA")}
                    />
                    <div className="AlignPopupParameterSelector-measurement-container">
                      <IncrementDecrementButtons
                        onIncrement={() =>
                          startIncrement(
                            t1paIncrementRef,
                            setT1paValue,
                            t1paRange
                          )
                        }
                        onDecrement={() =>
                          startDecrement(
                            t1paDecrementRef,
                            setT1paValue,
                            t1paRange
                          )
                        }
                        onIncrementEnd={() => stopChange(t1paIncrementRef)}
                        onDecrementEnd={() => stopChange(t1paDecrementRef)}
                        disabled={isConfirming || !isSaasT1paValid}
                      />
                      <MeasurementDisplay
                        value={isSaasT1paValid ? t1paValue : NaN}
                        unit="°"
                        disabled={isConfirming || !isSaasT1paValid}
                      />
                    </div>
                  </div>
                  <div className="AlignPopupParameterSelector-second-row App-text-medium">
                    SAAS:{" "}
                    {saasGlobalAlignment?.t1pa !== undefined &&
                    saasGlobalAlignment?.t1pa !== null
                      ? `${Math.round(saasGlobalAlignment.t1pa)}°`
                      : "--"}{" "}
                    | Original:{" "}
                    {preOpGlobalAlignment?.t1pa !== undefined &&
                    preOpGlobalAlignment?.t1pa !== null
                      ? `${Math.round(preOpGlobalAlignment.t1pa)}°`
                      : "--"}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="AlignPopupParameterSelector-item">
                  <div className="AlignPopupParameterSelector-first-row">
                    <LabeledSlider
                      label="LL(L1S1)"
                      min={lumbarLordosisRange?.min ?? 0}
                      max={lumbarLordosisRange?.max ?? 0}
                      value={lumbarLordosisValue}
                      type={LABELED_SLIDER_TYPES.HORIZONTAL}
                      onChange={handleLumbarLordosisChange}
                      disabled={
                        isConfirming || !isGapLumbarLordosisValid || !isPiValid
                      }
                      markers={lumbarLordosisMarkers}
                      tooltip={getSppTooltip("LL")}
                    />
                    <div className="AlignPopupParameterSelector-measurement-container">
                      <IncrementDecrementButtons
                        onIncrement={() =>
                          startIncrement(
                            lumbarLordosisIncrementRef,
                            setLumbarLordosisValue,
                            lumbarLordosisRange
                          )
                        }
                        onDecrement={() =>
                          startDecrement(
                            lumbarLordosisDecrementRef,
                            setLumbarLordosisValue,
                            lumbarLordosisRange
                          )
                        }
                        onIncrementEnd={() =>
                          stopChange(lumbarLordosisIncrementRef)
                        }
                        onDecrementEnd={() =>
                          stopChange(lumbarLordosisDecrementRef)
                        }
                        disabled={
                          isConfirming || !isGapLumbarLordosisValid || !isPiValid
                        }
                      />
                      <MeasurementDisplay
                        value={
                          isGapLumbarLordosisValid && isPiValid
                            ? lumbarLordosisValue
                            : NaN
                        }
                        unit="°"
                        disabled={
                          isConfirming || !isGapLumbarLordosisValid || !isPiValid
                        }
                      />
                    </div>
                  </div>
                  <div className="AlignPopupParameterSelector-second-row App-text-medium">
                    GAP:{" "}
                    {gapGlobalAlignment?.lumbarLordosis !== undefined &&
                    gapGlobalAlignment?.lumbarLordosis !== null
                      ? `${Math.round(gapGlobalAlignment.lumbarLordosis)}°`
                      : "--"}{" "}
                    | Original:{" "}
                    {preOpGlobalAlignment?.lumbarLordosis !== undefined &&
                    preOpGlobalAlignment?.lumbarLordosis !== null
                      ? `${Math.round(preOpGlobalAlignment.lumbarLordosis)}°`
                      : "--"}
                  </div>
                </div>

                <div className="AlignPopupParameterSelector-item">
                  <div className="AlignPopupParameterSelector-first-row">
                    <LabeledSlider
                      label="SS"
                      min={sacralSlopeRange.min}
                      max={sacralSlopeRange.max}
                      value={sacralSlopeValue}
                      type={LABELED_SLIDER_TYPES.HORIZONTAL}
                      onChange={handleSacralSlopeChange}
                      disabled={isConfirming || !isGapSacralSlopeValid}
                      markers={sacralSlopeMarkers}
                      tooltip={getSppTooltip("SS")}
                    />
                    <div className="AlignPopupParameterSelector-measurement-container">
                      <IncrementDecrementButtons
                        onIncrement={() =>
                          startIncrement(
                            sacralSlopeIncrementRef,
                            setSacralSlopeValue,
                            sacralSlopeRange
                          )
                        }
                        onDecrement={() =>
                          startDecrement(
                            sacralSlopeDecrementRef,
                            setSacralSlopeValue,
                            sacralSlopeRange
                          )
                        }
                        onIncrementEnd={() =>
                          stopChange(sacralSlopeIncrementRef)
                        }
                        onDecrementEnd={() =>
                          stopChange(sacralSlopeDecrementRef)
                        }
                        disabled={isConfirming || !isGapSacralSlopeValid}
                      />
                      <MeasurementDisplay
                        value={isGapSacralSlopeValid ? sacralSlopeValue : NaN}
                        unit="°"
                        disabled={isConfirming || !isGapSacralSlopeValid}
                      />
                    </div>
                  </div>
                  <div className="AlignPopupParameterSelector-second-row App-text-medium">
                    GAP:{" "}
                    {gapGlobalAlignment?.sacralSlope !== undefined &&
                    gapGlobalAlignment?.sacralSlope !== null
                      ? `${Math.round(gapGlobalAlignment.sacralSlope)}°`
                      : "--"}{" "}
                    | Original:{" "}
                    {preOpGlobalAlignment?.sacralSlope !== undefined &&
                    preOpGlobalAlignment?.sacralSlope !== null
                      ? `${Math.round(preOpGlobalAlignment.sacralSlope)}°`
                      : "--"}
                  </div>
                </div>
                <div className="AlignPopupParameterSelector-item">
                  <div className="AlignPopupParameterSelector-first-row">
                    <LabeledSlider
                      label="GT"
                      min={globalTiltRange.min}
                      max={globalTiltRange.max}
                      value={globalTiltValue}
                      type={LABELED_SLIDER_TYPES.HORIZONTAL}
                      onChange={handleGlobalTiltChange}
                      disabled={isConfirming || !isGapGlobalTiltValid}
                      markers={globalTiltMarkers}
                      tooltip={getSppTooltip("GT")}
                    />
                    <div className="AlignPopupParameterSelector-measurement-container">
                      <IncrementDecrementButtons
                        onIncrement={() =>
                          startIncrement(
                            globalTiltIncrementRef,
                            setGlobalTiltValue,
                            globalTiltRange
                          )
                        }
                        onDecrement={() =>
                          startDecrement(
                            globalTiltDecrementRef,
                            setGlobalTiltValue,
                            globalTiltRange
                          )
                        }
                        onIncrementEnd={() =>
                          stopChange(globalTiltIncrementRef)
                        }
                        onDecrementEnd={() =>
                          stopChange(globalTiltDecrementRef)
                        }
                        disabled={isConfirming || !isGapGlobalTiltValid}
                      />
                      <MeasurementDisplay
                        value={isGapGlobalTiltValid ? globalTiltValue : NaN}
                        unit="°"
                        disabled={isConfirming || !isGapGlobalTiltValid}
                      />
                    </div>
                  </div>
                  <div className="AlignPopupParameterSelector-second-row App-text-medium">
                    GAP:{" "}
                    {gapGlobalAlignment?.globalTilt !== undefined &&
                    gapGlobalAlignment?.globalTilt !== null
                      ? `${Math.round(gapGlobalAlignment.globalTilt)}°`
                      : "--"}{" "}
                    | Original:{" "}
                    {preOpGlobalAlignment?.globalTilt !== undefined &&
                    preOpGlobalAlignment?.globalTilt !== null
                      ? `${Math.round(preOpGlobalAlignment.globalTilt)}°`
                      : "--"}
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="AlignPopupParameterSelector-decompensation-section">
            <div className="AlignPopupParameterSelector-decompensation-title-section">
              <div className="App-title-widget">
                Remove Compensation Mechanism:
              </div>
              <IconButton
                icon={IconsEnum.INFO}
                title={t("461")}
                onClick={e => e.stopPropagation()}
                isCircle
                size={ButtonSize.MICRO}
                isLightBlue={false}
                tooltipOrientation={TooltipOrientation.Right}
              />
            </div>
            <div className="AlignPopupParameterSelector-item">
              <div className="AlignPopupParameterSelector-first-row">
                <LabeledSlider
                  label="Pelvic Tilt"
                  min={pelvicTiltRange.min}
                  max={pelvicTiltRange.max}
                  value={pelvicTiltValue}
                  type={LABELED_SLIDER_TYPES.HORIZONTAL}
                  onChange={handlePelvicTiltChange}
                  disabled={isConfirming || !isPreopPelvicTiltValid}
                  markers={pelvicTiltMarkers}
                />
                <div className="AlignPopupParameterSelector-measurement-container">
                  <IncrementDecrementButtons
                    onIncrement={() =>
                      startIncrement(
                        pelvicTiltIncrementRef,
                        setPelvicTiltValue,
                        pelvicTiltRange
                      )
                    }
                    onDecrement={() =>
                      startDecrement(
                        pelvicTiltDecrementRef,
                        setPelvicTiltValue,
                        pelvicTiltRange
                      )
                    }
                    onIncrementEnd={() => stopChange(pelvicTiltIncrementRef)}
                    onDecrementEnd={() => stopChange(pelvicTiltDecrementRef)}
                    disabled={isConfirming || !isPreopPelvicTiltValid}
                  />
                  <MeasurementDisplay
                    value={isPreopPelvicTiltValid ? pelvicTiltValue : NaN}
                    unit="°"
                    disabled={isConfirming || !isPreopPelvicTiltValid}
                  />
                </div>
              </div>
              <div className="AlignPopupParameterSelector-second-row App-text-medium">
                {baseSelectedMethod === TargetTypeEnum.SAAS ? "SAAS" : "GAP"}:{" "}
                {baseSelectedMethod === TargetTypeEnum.SAAS
                  ? saasGlobalAlignment?.pelvicTilt !== undefined &&
                    saasGlobalAlignment?.pelvicTilt !== null
                    ? `${Math.round(saasGlobalAlignment.pelvicTilt)}°`
                    : "--"
                  : gapGlobalAlignment?.pelvicTilt !== undefined &&
                      gapGlobalAlignment?.pelvicTilt !== null
                    ? `${Math.round(gapGlobalAlignment.pelvicTilt)}°`
                    : "--"}{" "}
                | Original:{" "}
                {preOpGlobalAlignment?.pelvicTilt !== undefined &&
                preOpGlobalAlignment?.pelvicTilt !== null
                  ? `${Math.round(preOpGlobalAlignment.pelvicTilt)}°`
                  : "--"}
              </div>
            </div>

            <div className="AlignPopupParameterSelector-item">
              <div className="AlignPopupParameterSelector-first-row">
                <LabeledSlider
                  label="Knee Angle"
                  min={kneeAngleRange.min}
                  max={kneeAngleRange.max}
                  value={kneeAngleValue}
                  type={LABELED_SLIDER_TYPES.HORIZONTAL}
                  onChange={handleKneeAngleChange}
                  disabled={isConfirming || !isPreopKneeAngleValid}
                  markers={kneeAngleMarkers}
                />
                <div className="AlignPopupParameterSelector-measurement-container">
                  <IncrementDecrementButtons
                    onIncrement={() =>
                      startIncrement(
                        kneeAngleIncrementRef,
                        setKneeAngleValue,
                        kneeAngleRange
                      )
                    }
                    onDecrement={() =>
                      startDecrement(
                        kneeAngleDecrementRef,
                        setKneeAngleValue,
                        kneeAngleRange
                      )
                    }
                    onIncrementEnd={() => stopChange(kneeAngleIncrementRef)}
                    onDecrementEnd={() => stopChange(kneeAngleDecrementRef)}
                    disabled={isConfirming || !isPreopKneeAngleValid}
                  />
                  <MeasurementDisplay
                    value={isPreopKneeAngleValid ? kneeAngleValue : NaN}
                    unit="°"
                    disabled={isConfirming || !isPreopKneeAngleValid}
                  />
                </div>
              </div>
              <div className="AlignPopupParameterSelector-second-row App-text-medium">
                Suggested: {SUGGESTED_KNEE_ANGLE}° | Original:{" "}
                {preOpGlobalAlignment?.kneeAngle !== undefined &&
                preOpGlobalAlignment?.kneeAngle !== null
                  ? `${Math.round(preOpGlobalAlignment.kneeAngle)}°`
                  : "--"}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="AlignPopupParameterSelector-button-wrapper">
        <Button
          value={
            isConfirming
              ? "Confirming..."
              : hasChanges
                ? "Confirm"
                : "No Changes"
          }
          onClick={handleConfirm}
          disabled={isConfirmDisabled}
        />
      </div>
    </div>
  );
};

export default GlobalAlignment;
